import {
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { prefersReducedMotion } from '../../../../core/utils/motion.util';

@Component({
  selector: 'app-compound-section',
  standalone: true,
  template: `
    <section class="landing-section compound">
      <div class="landing-inner">
        <div class="compound-grid">
          <div class="enso-stage reveal" #ensoStage>
            <svg viewBox="0 0 400 400" aria-hidden="true">
              <path
                class="enso-stroke"
                [style.stroke-dasharray]="ensoDasharray()"
                [style.stroke-dashoffset.px]="ensoOffset()"
                d="M 232 38
                   C 130 22, 38 102, 42 206
                   C 46 312, 142 376, 240 360
                   C 338 344, 380 250, 356 162
                   C 338 96, 286 56, 244 48"
              />
            </svg>
            <div class="enso-center">
              <div class="day-label">Day</div>
              <div class="day-num">{{ dayDisplay() }}</div>
              <div class="day-sub">of always</div>
            </div>
            <p class="enso-caption">
              The ensō never closes.
              <span class="em">Neither does growth.</span>
            </p>
          </div>

          <div class="reveal" data-delay="1">
            <div class="landing-kicker">
              <span class="brush"></span>The kaizen math
            </div>
            <h2 class="landing-h2">One percent better, every day.</h2>
            <p class="landing-sub">
              Improvement doesn't add up. It compounds. Not one heroic leap —
              365 small, deliberate steps. That's the difference between
              stalling and 37.8×.
            </p>
            <p class="math-line">
              1.01<sup>365</sup> = <span class="v">37.8×</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  `,
  styleUrl: './compound-section.component.scss',
})
export class CompoundSectionComponent {
  private readonly ensoStage = viewChild<ElementRef<HTMLElement>>('ensoStage');
  private readonly destroyRef = inject(DestroyRef);

  readonly ensoLength = signal(0);
  readonly ensoOffset = signal(0);
  readonly dayDisplay = signal('1');

  readonly ensoDasharray = () => `${this.ensoLength()}px`;

  private dayTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    afterNextRender(() => {
      const stage = this.ensoStage()?.nativeElement;
      if (!stage) return;

      const path = stage.querySelector('path');
      if (!path) return;

      const length = path.getTotalLength();
      this.ensoLength.set(length);
      this.ensoOffset.set(length);

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            if (prefersReducedMotion()) {
              this.ensoOffset.set(0);
              this.dayDisplay.set('∞');
            } else {
              this.runEnsoAnimation();
            }

            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.5 },
      );

      observer.observe(stage);
      this.destroyRef.onDestroy(() => {
        observer.disconnect();
        if (this.dayTimer) clearInterval(this.dayTimer);
      });
    });
  }

  private runEnsoAnimation(): void {
    this.ensoOffset.set(0);

    let day = 1;
    this.dayTimer = setInterval(() => {
      day += Math.ceil(day * 0.08);
      if (day >= 365) {
        this.dayDisplay.set('∞');
        if (this.dayTimer) clearInterval(this.dayTimer);
        return;
      }
      this.dayDisplay.set(String(day));
    }, 90);
  }
}
